import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, Search, Filter, X, RefreshCw } from "lucide-react";

// Aggregated audit feed: admin actions + transactional emails + Stripe events.
// Backed by GET /api/admin/audit-log.
export default function AuditTab() {
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [eventType, setEventType] = useState("");
  const [q, setQ] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [limit, setLimit] = useState(100);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams();
      if (eventType) params.set("event_type", eventType);
      if (q) params.set("q", q);
      if (since) params.set("since", since);
      if (until) params.set("until", until);
      if (limit) params.set("limit", String(limit));
      const { data } = await api.get(`/admin/audit-log?${params.toString()}`);
      setEvents(data.events || []);
      setEventTypes(data.event_types || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [eventType, q, since, until, limit]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => {
    setEventType(""); setQ(""); setSince(""); setUntil("");
  };

  const eventTypeOptions = useMemo(() => {
    const set = new Set(eventTypes);
    // Always include the currently-selected one even if filtered out.
    if (eventType) set.add(eventType);
    return Array.from(set).sort();
  }, [eventTypes, eventType]);

  return (
    <div className="space-y-4" data-testid="audit-tab">
      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 text-xs font-mono">
        <FilterField label="event type">
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            data-testid="audit-filter-type"
            className="bg-black border border-zinc-800 px-2 py-1.5 text-zinc-200 focus:border-[#00E5FF] outline-none"
          >
            <option value="">all</option>
            {eventTypeOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </FilterField>
        <FilterField label="user email contains">
          <div className="relative">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. jane@"
              data-testid="audit-filter-q"
              className="bg-black border border-zinc-800 pl-6 pr-2 py-1.5 text-zinc-200 focus:border-[#00E5FF] outline-none w-48"
            />
          </div>
        </FilterField>
        <FilterField label="since (UTC)">
          <input
            type="date"
            value={since.slice(0, 10)}
            onChange={(e) => setSince(e.target.value ? `${e.target.value}T00:00:00+00:00` : "")}
            data-testid="audit-filter-since"
            className="bg-black border border-zinc-800 px-2 py-1.5 text-zinc-200 focus:border-[#00E5FF] outline-none"
          />
        </FilterField>
        <FilterField label="until (UTC)">
          <input
            type="date"
            value={until.slice(0, 10)}
            onChange={(e) => setUntil(e.target.value ? `${e.target.value}T23:59:59+00:00` : "")}
            data-testid="audit-filter-until"
            className="bg-black border border-zinc-800 px-2 py-1.5 text-zinc-200 focus:border-[#00E5FF] outline-none"
          />
        </FilterField>
        <FilterField label="limit">
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            data-testid="audit-filter-limit"
            className="bg-black border border-zinc-800 px-2 py-1.5 text-zinc-200 focus:border-[#00E5FF] outline-none"
          >
            {[50, 100, 250, 500].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </FilterField>
        <button
          onClick={clearFilters}
          className="border border-zinc-800 hover:border-[#00E5FF] hover:text-[#00E5FF] text-zinc-400 px-2.5 py-1.5 flex items-center gap-1.5 uppercase tracking-widest"
          data-testid="audit-clear-filters"
        >
          <X size={11} /> clear
        </button>
        <button
          onClick={load}
          className="border border-zinc-800 hover:border-[#00E5FF] hover:text-[#00E5FF] text-zinc-400 px-2.5 py-1.5 flex items-center gap-1.5 uppercase tracking-widest"
          data-testid="audit-refresh"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> refresh
        </button>
        <div className="ml-auto text-[10px] text-zinc-500 uppercase tracking-widest">
          {events.length} event{events.length === 1 ? "" : "s"}
        </div>
      </div>

      {err && (
        <div className="text-xs text-[#EF4444] font-mono border border-[#EF4444]/40 bg-[#EF4444]/10 px-3 py-2" data-testid="audit-error">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="border border-zinc-900 overflow-x-auto">
        <table className="w-full text-xs font-mono" data-testid="audit-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-900 bg-zinc-950">
              <th className="px-3 py-2 w-40">Timestamp</th>
              <th className="px-3 py-2 w-48">Event Type</th>
              <th className="px-3 py-2 w-56">User</th>
              <th className="px-3 py-2">Details</th>
              <th className="px-3 py-2 w-48">Actor</th>
            </tr>
          </thead>
          <tbody>
            {loading && events.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-10 text-center text-zinc-500">
                <Loader2 className="inline-block animate-spin mr-2" size={14} /> loading audit feed…
              </td></tr>
            ) : events.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-10 text-center text-zinc-600 italic">no events match the current filters</td></tr>
            ) : events.map((e, i) => (
              <tr key={i} className="border-b border-zinc-900/60 hover:bg-zinc-950" data-testid={`audit-row-${i}`}>
                <td className="px-3 py-2 text-zinc-400">{formatTs(e.ts)}</td>
                <td className="px-3 py-2"><EventBadge type={e.event_type} /></td>
                <td className="px-3 py-2 text-zinc-200 break-all">{e.user_email || <span className="text-zinc-600">—</span>}</td>
                <td className="px-3 py-2 text-zinc-300">{e.details || <span className="text-zinc-600">—</span>}</td>
                <td className="px-3 py-2 text-zinc-400 break-all">{e.actor || <span className="text-zinc-600">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FilterField = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
      <Filter size={9} /> {label}
    </span>
    {children}
  </label>
);

function EventBadge({ type }) {
  const color = (() => {
    if (!type) return "border-zinc-700 text-zinc-400";
    if (type.startsWith("user.delete") || type.startsWith("user.bulk_delete")) return "border-[#EF4444]/60 text-[#EF4444]";
    if (type.startsWith("user.upgrade")) return "border-[#22c55e]/60 text-[#22c55e]";
    if (type.startsWith("user.downgrade")) return "border-[#F59E0B]/60 text-[#F59E0B]";
    if (type.startsWith("payment")) return "border-[#00E5FF]/60 text-[#00E5FF]";
    if (type.startsWith("email")) return "border-fuchsia-500/60 text-fuchsia-300";
    if (type.startsWith("password_reset")) return "border-amber-500/60 text-amber-300";
    return "border-zinc-700 text-zinc-300";
  })();
  return (
    <span className={`inline-block border px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${color}`}>
      {type || "—"}
    </span>
  );
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    // YYYY-MM-DD HH:MM:SS UTC
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  } catch {
    return iso;
  }
}
